import { Component, type ReactNode } from 'react';

/** Bilingual helper — safe for class components (no hooks). */
function t(ar: string, en: string): string {
  return document.documentElement.lang === 'ar' ? ar : en;
}

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: { componentStack: string }) {
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <div className="flex flex-col items-center justify-center min-h-[40vh] gap-4 p-8 text-center">
          <p className="text-2xl">⚠️</p>
          <h2 className="text-lg font-semibold text-gray-800">
            {t('حدث خطأ غير متوقع', 'Something went wrong')}
          </h2>
          <p className="text-sm text-gray-500 max-w-sm">
            {this.state.error?.message ?? t('حدث خطأ غير متوقع.', 'An unexpected error occurred.')}
          </p>
          <button
            onClick={() => this.setState({ hasError: false, error: null })}
            className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            {t('حاول مجدداً', 'Try again')}
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
